import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, ChevronDown, LayoutDashboard, User, Building2, Award, CreditCard, Clock, Sparkles, Briefcase, Users, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import NotificationBell from './NotificationBell';
import GlobalSearchBar from './GlobalSearchBar';
import PrefetchLink from './PrefetchLink';
import LanguageSwitcher from './LanguageSwitcher';
import { setUserContext } from '../lib/analytics';
import useTrialStatus from '../hooks/useTrialStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const [user, setUser] = useState(null);
  // Mobile-header profile popover open state (Twitter/Threads-style avatar tap)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  // Trial / subscription status — only fetched when a user is logged in.
  // The hook returns null for anonymous + paid users get is_paid=true.
  const { status: trialStatus } = useTrialStatus({ enabled: !!user });

  // Compute the tier-aware CTA shown at the top of the mobile profile popover.
  // Returns one of: trial-active, trial-expired, paid-with-expiry, free, or null.
  const tierCta = useMemo(() => {
    if (!user) return null;
    if (trialStatus?.state === 'trial_active') {
      const hrs = trialStatus.hours_remaining || 0;
      const days = Math.max(0, Math.floor(hrs / 24));
      const label = days >= 1 ? `${days} day${days === 1 ? '' : 's'} left` : `${Math.max(1, hrs)}h left`;
      return { kind: 'trial', label, cta: 'Upgrade', to: '/membership' };
    }
    if (trialStatus?.state === 'trial_expired') {
      return { kind: 'expired', label: 'Trial ended', cta: 'Upgrade now', to: '/membership' };
    }
    if (trialStatus?.is_paid || trialStatus?.state === 'paid') {
      const exp = user.membership_expires_at;
      const expStr = exp
        ? new Date(exp).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
        : null;
      return { kind: 'paid', label: expStr ? `Active · expires ${expStr}` : 'Active', cta: 'Renew', to: '/membership' };
    }
    // Anonymous-ish free tier (registered but no trial row — shouldn't happen, but safe fallback)
    return { kind: 'free', label: 'Free tier', cta: 'Upgrade', to: '/membership' };
  }, [user, trialStatus]);
  const navigate = useNavigate();
  const location = useLocation();
  const exploreRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { checkAuth(); }, [location.pathname]);

  // Close the Explore mega-menu on any route change OR click outside
  useEffect(() => { setIsExploreOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!isExploreOpen) return undefined;
    const onClick = (e) => {
      if (exploreRef.current && !exploreRef.current.contains(e.target)) {
        setIsExploreOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setIsExploreOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isExploreOpen]);
  // Same close-on-route-change + click-outside for mobile profile popover
  useEffect(() => { setIsProfileMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!isProfileMenuOpen) return undefined;
    const onClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setIsProfileMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isProfileMenuOpen]);


  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // dataLayer — push user_tier so GTM audiences can segment by membership
      setUserContext({ user_tier: response.data?.membership_type || 'free' });
    } catch (error) {
      setUser(null);
      setUserContext({ user_tier: 'anonymous', trial_state: 'none' });
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Top-bar always-visible primary nav (kept minimal — 4 links + Explore dropdown).
  const primaryLinks = [
    { name: t('nav.about'),      path: '/about',      key: 'about' },
    { name: t('nav.membership'), path: '/membership', key: 'membership' },
    { name: t('nav.events'),     path: '/events',     key: 'events' },
    { name: t('nav.blog'),       path: '/blog',       key: 'blog' },
  ];

  // Explore mega-dropdown — 3 columns, 8 secondary links.
  const exploreGroups = [
    {
      heading: t('nav.group_business', 'For Business'),
      icon: Briefcase,
      links: [
        { name: t('nav.investments'),  path: '/investments',        key: 'investments' },
        { name: t('nav.investors'),    path: '/investor-offers',    key: 'investors' },
        { name: 'Deal Room', path: '/deal-room', key: 'deal-room' },
        { name: 'Real Estate Exit', path: '/real-estate-exit', key: 'real-estate-exit' },
        { name: t('nav.businesses'),   path: '/business-directory', key: 'businesses' },
        { name: 'Business Support Services', path: '/services', key: 'services' },
        { name: 'Govt. Benefits', path: '/gov-benefits', key: 'gov-benefits' },
      ],
    },
    {
      heading: t('nav.group_members', 'For Members'),
      icon: Users,
      links: [
        { name: t('nav.benefits'), path: '/benefits',     key: 'benefits' },
        { name: t('nav.chapters'), path: '/city-chapters', key: 'chapters' },
        { name: t('nav.members'),  path: '/members',       key: 'members' },
      ],
    },
    {
      heading: t('nav.group_engage', 'Engage'),
      icon: MessageSquare,
      links: [
        { name: t('nav.communities'), path: '/communities', key: 'communities' },
        { name: 'Special Clubs', path: '/clubs', key: 'clubs' },
        { name: 'Mentors', path: '/mentors', key: 'mentors' },
        { name: 'Masterminds', path: '/masterminds', key: 'masterminds' },
        { name: 'Jobs', path: '/jobs', key: 'jobs' },
        { name: 'Partners', path: '/partners', key: 'partners' },
        { name: t('nav.impact'),      path: '/impact',      key: 'impact' },
      ],
    },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-slate-950/95 backdrop-blur-lg border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 shrink-0" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#d4af37] flex items-center justify-center">
              <span className="text-slate-950 font-bold text-xl font-playfair">IB</span>
            </div>
            <span className="text-xl font-playfair font-bold text-white tracking-wide whitespace-nowrap">
              IB Club
            </span>
          </Link>

          {/* Desktop Primary Nav */}
          <div className="hidden lg:flex items-center space-x-5 flex-1 justify-center">
            <PrefetchLink
              to="/about"
              data-testid="nav-about"
              className="text-sm font-medium text-slate-300 hover:text-[#d4af37] transition-colors tracking-wide whitespace-nowrap"
            >
              {t('nav.about')}
            </PrefetchLink>
            <PrefetchLink
              to="/membership"
              data-testid="nav-membership"
              className="text-sm font-medium text-slate-300 hover:text-[#d4af37] transition-colors tracking-wide whitespace-nowrap"
            >
              {t('nav.membership')}
            </PrefetchLink>
            <PrefetchLink
              to="/events"
              data-testid="nav-events"
              className="text-sm font-medium text-slate-300 hover:text-[#d4af37] transition-colors tracking-wide whitespace-nowrap"
            >
              {t('nav.events')}
            </PrefetchLink>

            {/* Explore mega-menu */}
            <div ref={exploreRef} className="relative">
              <button
                type="button"
                onClick={() => setIsExploreOpen((v) => !v)}
                data-testid="nav-explore-toggle"
                aria-haspopup="true"
                aria-expanded={isExploreOpen}
                className={`text-sm font-medium hover:text-[#d4af37] transition-colors tracking-wide whitespace-nowrap inline-flex items-center gap-1 ${
                  isExploreOpen ? 'text-[#d4af37]' : 'text-slate-300'
                }`}
              >
                {t('nav.explore', 'Explore')}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${isExploreOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isExploreOpen && (
                <div
                  data-testid="nav-explore-menu"
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[680px] bg-slate-950 border border-white/10 shadow-2xl shadow-black/40 backdrop-blur-xl"
                >
                  {/* Subtle gold accent strip */}
                  <div className="h-px bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent" />
                  <div className="grid grid-cols-3 gap-2 p-6">
                    {exploreGroups.map((g) => (
                      <div key={g.heading} className="space-y-1.5">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#d4af37]/70 font-semibold mb-2.5">
                          {g.heading}
                        </p>
                        {g.links.map((link) => (
                          <PrefetchLink
                            key={link.path}
                            to={link.path}
                            data-testid={`nav-${link.key}`}
                            onClick={() => setIsExploreOpen(false)}
                            className="block text-[13px] text-slate-300 hover:text-[#d4af37] hover:translate-x-0.5 transition-all duration-200 py-1"
                          >
                            {link.name}
                          </PrefetchLink>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <PrefetchLink
              to="/blog"
              data-testid="nav-blog"
              className="text-sm font-medium text-slate-300 hover:text-[#d4af37] transition-colors tracking-wide whitespace-nowrap"
            >
              {t('nav.blog')}
            </PrefetchLink>
          </div>

          {/* Auth Section */}
          <div className="hidden lg:flex items-center space-x-3 shrink-0">
            {user ? (
              <>
                <GlobalSearchBar />
                <NotificationBell user={user} />
                <LanguageSwitcher variant="compact" />
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="text-sm text-slate-300 hover:text-[#d4af37] transition-colors whitespace-nowrap"
                >
                  {t('nav.dashboard')}
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  aria-label={t('nav.logout')}
                  className="text-slate-300 hover:text-[#d4af37] transition-colors p-1"
                >
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <>
                <GlobalSearchBar />
                <LanguageSwitcher variant="compact" />
                <PrefetchLink
                  to="/login"
                  data-testid="login-button"
                  className="text-sm text-slate-300 hover:text-white transition-colors whitespace-nowrap"
                >
                  {t('nav.login')}
                </PrefetchLink>
                <PrefetchLink
                  to="/register"
                  data-testid="join-now-button"
                  title="Start your 3-day free trial — no credit card required"
                  className="bg-[#d4af37] text-slate-950 hover:bg-[#e5c558] font-semibold px-5 py-2 tracking-wide uppercase text-xs transition-all duration-300 whitespace-nowrap"
                >
                  Start free trial
                </PrefetchLink>
              </>
            )}
          </div>

          {/* Mobile auth controls — visible on every page next to hamburger.
              Shown BEFORE the menu button so users don't have to open the
              drawer just to access notifications, dashboard, or login. */}
          <div className="lg:hidden flex items-center gap-2 shrink-0" ref={profileMenuRef}>
            {user ? (
              <>
                <div data-testid="mobile-header-notification-bell">
                  <NotificationBell user={user} />
                </div>
                <button
                  type="button"
                  onClick={() => setIsProfileMenuOpen((v) => !v)}
                  data-testid="mobile-header-avatar"
                  aria-label="Open profile menu"
                  aria-expanded={isProfileMenuOpen}
                  className="flex items-center"
                >
                  {user.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={user.name || 'Profile'}
                      className="w-9 h-9 rounded-full object-cover ring-2 ring-[#d4af37]/40 hover:ring-[#d4af37] transition"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#d4af37]/20 ring-2 ring-[#d4af37]/40 flex items-center justify-center text-[#d4af37] text-sm font-semibold">
                      {(user.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                </button>

                {/* Profile popover — Twitter/Threads-style. Anchored to avatar,
                    safe-gutter pinned on narrow viewports. */}
                {isProfileMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsProfileMenuOpen(false)}
                    />
                    <div
                      data-testid="mobile-profile-menu"
                      className="
                        fixed left-3 right-3 top-16 z-50 rounded-xl
                        bg-slate-900 border border-[#d4af37]/30 shadow-2xl overflow-hidden
                        sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-64
                      "
                    >
                      {/* User identity strip */}
                      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                        {user.profile_picture ? (
                          <img src={user.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#d4af37]/20 flex items-center justify-center text-[#d4af37] font-semibold">
                            {(user.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate" data-testid="mobile-profile-menu-name">{user.name}</div>
                          <div className="text-xs text-slate-400 capitalize">{user.membership_type || 'member'} member</div>
                        </div>
                      </div>

                      {/* Tier-aware CTA strip — trial countdown, expired nudge,
                          or paid expiry+renew. Driven by useTrialStatus() so it
                          updates live every 5 min (every 60s in the last 24h). */}
                      {tierCta && (
                        <Link
                          to={tierCta.to}
                          data-testid="mobile-profile-menu-tier-cta"
                          className={`flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 transition-colors text-sm ${
                            tierCta.kind === 'trial' || tierCta.kind === 'expired'
                              ? 'bg-amber-500/10 hover:bg-amber-500/15 text-amber-200'
                              : tierCta.kind === 'paid'
                              ? 'bg-emerald-500/10 hover:bg-emerald-500/15 text-emerald-200'
                              : 'bg-slate-800/50 hover:bg-slate-800 text-slate-200'
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            {tierCta.kind === 'paid' ? <Sparkles className="w-4 h-4 shrink-0" /> : <Clock className="w-4 h-4 shrink-0" />}
                            <span className="truncate" data-testid="mobile-profile-menu-tier-label">{tierCta.label}</span>
                          </span>
                          <span className="font-semibold shrink-0">{tierCta.cta} →</span>
                        </Link>
                      )}


                      {/* Menu rows */}
                      <ProfileMenuRow to="/dashboard" icon={<LayoutDashboard className="w-4 h-4" />} label="Dashboard" testid="mobile-profile-menu-dashboard" />
                      <ProfileMenuRow to="/dashboard/profile" icon={<User className="w-4 h-4" />} label="My Profile" testid="mobile-profile-menu-profile" />
                      {/* Show "Upgrade tier" only for paid users below Elite. Trial/expired
                          users already see the prominent Upgrade CTA strip above. */}
                      {tierCta?.kind === 'paid' && (user.membership_type === 'basic' || user.membership_type === 'premium') && (
                        <ProfileMenuRow
                          to="/membership?upgrade=1"
                          icon={<Sparkles className="w-4 h-4" />}
                          label={user.membership_type === 'basic' ? 'Upgrade to Premium or Elite' : 'Upgrade to Elite'}
                          testid="mobile-profile-menu-upgrade"
                        />
                      )}
                      <ProfileMenuRow to="/dashboard/my-businesses" icon={<Building2 className="w-4 h-4" />} label="My Businesses" testid="mobile-profile-menu-businesses" />
                      <ProfileMenuRow to="/dashboard/refer" icon={<Award className="w-4 h-4" />} label="Invite & Earn ₹15k" testid="mobile-profile-menu-invite" />
                      <ProfileMenuRow to="/dashboard/payments" icon={<CreditCard className="w-4 h-4" />} label="Billing & Payments" testid="mobile-profile-menu-billing" />

                      {/* Logout */}
                      <button
                        type="button"
                        onClick={() => { setIsProfileMenuOpen(false); handleLogout(); }}
                        data-testid="mobile-profile-menu-logout"
                        className="w-full px-4 py-3 flex items-center gap-3 text-rose-300 hover:bg-rose-500/10 border-t border-white/10 transition-colors text-left text-sm"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <PrefetchLink
                to="/login"
                data-testid="mobile-header-login"
                className="text-sm font-medium text-[#d4af37] hover:text-white transition-colors px-2 py-1"
              >
                {t('nav.login')}
              </PrefetchLink>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle navigation menu"
              className="text-white p-1"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden py-5 border-t border-white/10 bg-slate-950 -mx-6 px-6 md:-mx-12 md:px-12 max-h-[calc(100vh-5rem)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            {/* Primary links — always flat, no accordion */}
            <div className="space-y-3 pb-4 border-b border-white/10">
              {primaryLinks.map((link) => (
                <PrefetchLink
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-slate-200 hover:text-[#d4af37] transition-colors text-sm font-medium"
                  data-testid={`mobile-link-${link.key}`}
                >
                  {link.name}
                </PrefetchLink>
              ))}
            </div>

            {/* Explore groups — collapsible accordions */}
            <div className="pt-2">
              {exploreGroups.map((g) => (
                <MobileAccordionGroup
                  key={g.heading}
                  heading={g.heading}
                  icon={g.icon}
                  links={g.links}
                  onLinkClick={() => setIsMobileMenuOpen(false)}
                  PrefetchLink={PrefetchLink}
                />
              ))}
            </div>

            {user ? (
              <div className="pt-4 mt-2 border-t border-white/10">
                <PrefetchLink
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-dashboard-link"
                  className="block text-slate-200 hover:text-[#d4af37] mb-3 text-sm font-medium"
                >
                  {t('nav.dashboard')}
                </PrefetchLink>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 text-slate-300 hover:text-[#d4af37] text-sm"
                >
                  <LogOut size={16} />
                  <span>{t('nav.logout')}</span>
                </button>
              </div>
            ) : null}
            {/* Login / Join Now / Language switcher live in the mobile header
                — not duplicated here. */}
          </div>
        )}
      </div>
    </nav>
  );
};

/**
 * Single row inside the mobile profile popover (Dashboard / Profile / etc.)
 * Kept as a tiny in-file helper since it's only used here.
 */
const ProfileMenuRow = ({ to, icon, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="w-full px-4 py-3 flex items-center gap-3 text-slate-200 hover:bg-white/5 transition-colors text-sm"
  >
    <span className="text-[#d4af37]">{icon}</span>
    <span>{label}</span>
  </Link>
);

/**
 * Collapsible section in the mobile drawer — heading toggles to reveal links.
 * Default: all groups collapsed (cleaner first impression than a long flat list).
 */
const MobileAccordionGroup = ({ heading, icon: Icon, links, onLinkClick, PrefetchLink }) => {
  const [open, setOpen] = useState(false);
  const slug = heading.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div className="border-b border-white/5 last:border-0" data-testid={`mobile-group-${slug}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-3 text-left text-slate-300 hover:text-[#d4af37] transition"
        data-testid={`mobile-group-toggle-${slug}`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2.5">
          {Icon && (
            <Icon
              size={15}
              className={`transition-colors ${open ? 'text-[#d4af37]' : 'text-slate-500'}`}
              strokeWidth={1.75}
            />
          )}
          <span className="text-xs uppercase tracking-widest font-semibold">{heading}</span>
        </span>
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180 text-[#d4af37]' : ''}`} />
      </button>
      {open && (
        <div className="pb-3 pl-7 space-y-2.5" data-testid={`mobile-group-links-${slug}`}>
          {links.map((link) => (
            <PrefetchLink
              key={link.path}
              to={link.path}
              onClick={onLinkClick}
              className="block text-slate-300 hover:text-[#d4af37] transition-colors text-sm"
              data-testid={`mobile-link-${link.key}`}
            >
              {link.name}
            </PrefetchLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default Navigation;
